import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { motion } from 'framer-motion';
import { ArrowLeft, ShieldCheck, Eye, EyeOff, Lock, Users2, GitCommit, AlertTriangle, CloudSun, Trees } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABEL = {
  pending_ai_review: 'Pending AI Review',
  high_confidence: 'High Confidence',
  community_verified: 'Community Verified',
  expert_verified: 'Expert Verified',
  research_verified: 'Research Grade',
  rejected: 'Rejected',
  archived: 'Archived',
};
const STATUS_COLOR = {
  pending_ai_review: '#9CA3AF', high_confidence: '#E9C46A',
  community_verified: '#95D5B2', expert_verified: '#40916C',
  research_verified: '#2A9D8F', rejected: '#EF4444', archived: '#6B7280',
};
const PRIVACY = [
  { value: 'private', label: 'Private (only you)', icon: Lock },
  { value: 'obscured', label: 'Obscured location', icon: EyeOff },
  { value: 'public', label: 'Public (full location)', icon: Eye },
  { value: 'research_only', label: 'Research Only', icon: ShieldCheck },
];

export default function ObservationDetail() {
  const { observationId } = useParams();
  const nav = useNavigate();
  const [obs, setObs] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingPrivacy, setSavingPrivacy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get(`/observations/${observationId}`),
        api.get(`/observations/${observationId}/history`).catch(() => ({ data: { events: [] } })),
      ]);
      setObs(a.data);
      setHistory(b.data?.events || []);
    } catch {
      toast.error('Observation not found');
      nav('/observations', { replace: true });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [observationId]); // eslint-disable-line

  const setPrivacy = async (val) => {
    if (!obs || savingPrivacy) return;
    setSavingPrivacy(true);
    try {
      const { data } = await api.patch(`/observations/${observationId}/privacy`, { privacy: val });
      setObs(data.observation);
      toast.success('Privacy updated');
      // reload history to show the new PRIVACY_CHANGE event
      const h = await api.get(`/observations/${observationId}/history`);
      setHistory(h.data?.events || []);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to update privacy');
    } finally { setSavingPrivacy(false); }
  };

  if (loading || !obs) return (
    <div className="min-h-screen bg-[#04120c] text-foreground flex items-center justify-center">
      <span className="text-white/60 text-sm">Loading…</span>
    </div>
  );

  const status = obs.verification_status || 'pending_ai_review';
  const isSensitive = !!obs.is_sensitive;

  return (
    <div className="min-h-screen bg-[#04120c] text-foreground pb-24" data-testid="observation-detail-root">
      <div className="max-w-2xl mx-auto px-4 py-4">
        <button onClick={() => nav(-1)} className="flex items-center gap-1 text-xs text-white/60 mb-3" data-testid="btn-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>

        {obs.image_path && (
          <img
            src={`${api.defaults.baseURL}/files/${obs.image_path}`}
            alt=""
            className="w-full aspect-square object-cover rounded-3xl border border-white/10 mb-4"
            data-testid="obs-hero-image"
          />
        )}

        <div className="flex items-center gap-1.5 mb-2 flex-wrap">
          <span
            data-testid="obs-status-badge"
            className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest"
            style={{ background: `${STATUS_COLOR[status]}22`, color: STATUS_COLOR[status] }}
          >{STATUS_LABEL[status]}</span>
          {isSensitive && (
            <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest bg-red-500/20 text-red-300 flex items-center gap-1">
              <AlertTriangle className="w-2.5 h-2.5" /> Sensitive Species
            </span>
          )}
        </div>

        <h1 className="font-serif text-3xl leading-tight" data-testid="obs-common-name">{obs.species_common_name}</h1>
        <p className="italic text-white/60">{obs.species_scientific_name}</p>

        {obs.ai_confidence != null && (
          <p className="text-[10px] uppercase tracking-widest text-white/40 mt-1">
            AI confidence: {Math.round(obs.ai_confidence * 100)}%
          </p>
        )}

        {/* Weather + Habitat chips (async-enriched) */}
        {(obs.weather || obs.habitat) && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="weather-habitat">
            {obs.weather?.weather_desc && (
              <span className="px-2.5 py-1 rounded-full glass border border-white/10 text-[11px] flex items-center gap-1.5">
                <CloudSun className="w-3 h-3 text-primary-light" />
                {obs.weather.weather_desc.replace(/_/g, ' ')}
                {obs.weather.temperature_c != null && ` · ${Math.round(obs.weather.temperature_c)}°C`}
              </span>
            )}
            {obs.weather?.season && (
              <span className="px-2.5 py-1 rounded-full glass border border-white/10 text-[11px] capitalize">
                {obs.weather.season}
              </span>
            )}
            {obs.habitat && (
              <span className="px-2.5 py-1 rounded-full glass border border-white/10 text-[11px] flex items-center gap-1.5">
                <Trees className="w-3 h-3 text-primary-light" />
                {obs.habitat.replace(/_/g, ' ')}
              </span>
            )}
          </div>
        )}

        {/* AI original vs current */}
        {obs.ai_original_identification && obs.current_identification &&
          obs.ai_original_identification.species_id !== obs.current_identification.species_id && (
          <div className="mt-3 p-3 rounded-2xl glass border border-white/10 text-xs" data-testid="obs-correction-note">
            <p className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Corrected</p>
            <p><span className="line-through text-white/50">{obs.ai_original_identification.species_common_name}</span>
              {' → '}<span className="text-primary-light">{obs.current_identification.species_common_name}</span></p>
          </div>
        )}

        {/* Privacy control (owner) */}
        <section className="mt-5 p-4 rounded-3xl glass border border-white/10" data-testid="privacy-controls">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-4 h-4 text-primary-light" />
            <h2 className="text-sm uppercase tracking-widest">Privacy</h2>
          </div>
          <div className="space-y-1.5">
            {PRIVACY.map(({ value, label, icon: Icon }) => {
              const active = obs.privacy === value;
              const disabled = isSensitive && value === 'public';
              return (
                <button
                  key={value}
                  data-testid={`privacy-option-${value}`}
                  disabled={disabled || savingPrivacy}
                  onClick={() => setPrivacy(value)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-full border text-xs text-left transition-colors ${
                    active ? 'bg-primary/20 border-primary text-white'
                    : 'glass border-white/10 text-white/80 hover:bg-white/5'
                  } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{label}</span>
                  {disabled && <span className="ml-auto text-[9px] uppercase tracking-widest text-red-300">Locked (sensitive)</span>}
                </button>
              );
            })}
          </div>
        </section>

        {/* Verification history */}
        <section className="mt-5 p-4 rounded-3xl glass border border-white/10" data-testid="verification-history">
          <div className="flex items-center gap-2 mb-3">
            <GitCommit className="w-4 h-4 text-primary-light" />
            <h2 className="text-sm uppercase tracking-widest">Audit Trail</h2>
            <span className="text-[10px] uppercase tracking-widest text-white/40 ml-auto">{history.length} events</span>
          </div>
          {history.length === 0 && <p className="text-xs text-white/50">No events yet.</p>}
          <ol className="space-y-2">
            {history.map((ev) => (
              <li key={ev.event_id} className="flex gap-3 text-xs" data-testid={`event-${ev.event_id}`}>
                <span className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: STATUS_COLOR[ev.new_status] || '#94A3B8' }} />
                <div className="min-w-0">
                  <p className="uppercase tracking-widest text-[9px] text-white/50">
                    {ev.action.replace(/_/g, ' ')} · {ev.verifier_role}
                    {ev.new_status && ` · → ${STATUS_LABEL[ev.new_status] || ev.new_status}`}
                  </p>
                  {ev.reason && <p className="text-white/80">{ev.reason}</p>}
                  {ev.notes && <p className="text-white/60 italic">{ev.notes}</p>}
                  {ev.previous_identification && ev.current_identification &&
                    ev.previous_identification.species_id !== ev.current_identification.species_id && (
                    <p className="text-white/70">
                      <span className="line-through text-white/40">{ev.previous_identification.species_common_name}</span>
                      {' → '}<span className="text-primary-light">{ev.current_identification.species_common_name}</span>
                    </p>
                  )}
                  <p className="text-[9px] uppercase tracking-widest text-white/30 mt-0.5">
                    {new Date(ev.created_at).toLocaleString()}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-5 p-4 rounded-3xl glass border border-white/10 flex items-center gap-2" data-testid="community-verify-cta">
          <Users2 className="w-4 h-4 text-primary-light" />
          <p className="text-xs text-white/70">
            Help verify observations from other explorers to earn contribution badges.
          </p>
          <button
            data-testid="btn-open-verification-queue"
            onClick={() => nav('/verify')}
            className="ml-auto px-3 py-1.5 rounded-full bg-primary text-white text-xs"
          >Open Queue</button>
        </section>
      </div>
    </div>
  );
}
